import { PropsWithChildren, useCallback, useMemo, useState } from 'react';

import { useFormikContext } from 'formik';

import { TargetAuthHeader } from '../../../../modules/Apps';
import { createNullableContext, useNullableContext } from '../../../../utils/Context';
import { TargetsFormValues, TargetAuthHeaderFormValue } from '../types';

interface TargetAuthHeaderContextValues {
  addAuthHeader: (values: Partial<TargetAuthHeaderFormValue>) => void;
  removeAuthHeader: (values: Partial<TargetAuthHeaderFormValue>) => void;
  authHeaders: TargetAuthHeaderFormValue[];
  authHeaderFormItem: Partial<TargetAuthHeaderFormValue>;
  setAuthHeaderFormItem: (value: Partial<TargetAuthHeaderFormValue>) => void;
  setEditItem: (value: Partial<TargetAuthHeaderFormValue>) => void;
  readOnly?: boolean;
}

interface TargetAuthHeaderContextProviderValues {
  readOnly?: boolean;
}

export const TargetAuthHeaderContext =
  createNullableContext<TargetAuthHeaderContextValues>('TargetAuthHeaderContext');

export const useTargetAuthHeaderContext = () => useNullableContext(TargetAuthHeaderContext);

export const TargetAuthHeaderContextProvider = ({
  children,
  readOnly,
}: PropsWithChildren<TargetAuthHeaderContextProviderValues>) => {
  const {
    values: { authHeaders },
    setFieldValue,
  } = useFormikContext<TargetsFormValues>();

  const [authHeaderFormItem, setAuthHeaderFormItem] = useState<Partial<TargetAuthHeaderFormValue>>({
    name: '',
    value: '',
  });

  const addAuthHeader = useCallback(
    (value: Partial<TargetAuthHeader>) => {
      setFieldValue(
        'authHeaders',
        authHeaders.some(authHeader => authHeader.name === value.name)
          ? authHeaders.map(authHeader => (authHeader.name === value.name ? value : authHeader))
          : [...authHeaders, value],
      );
    },
    [authHeaders, setFieldValue],
  );

  const removeAuthHeader = useCallback(
    (value: Partial<TargetAuthHeader>) => {
      const newHeaders = authHeaders.map(authHeader => {
        if (authHeader.name === value.name) {
          authHeader.removed = true;
        }

        return authHeader;
      });

      setFieldValue('authHeaders', newHeaders);
    },
    [authHeaders, setFieldValue],
  );

  const setEditItem = useCallback(
    (value: Partial<TargetAuthHeader>) => {
      const editingHeader = { ...value, editing: true };
      const items = authHeaders.map(item => {
        if (item.name === value.name) {
          return editingHeader;
        }

        return item;
      });

      setAuthHeaderFormItem(editingHeader);
      setFieldValue('authHeaders', items);
    },
    [authHeaders, setFieldValue],
  );

  const value = useMemo(
    () => ({
      addAuthHeader,
      removeAuthHeader,
      authHeaders,
      authHeaderFormItem,
      setAuthHeaderFormItem,
      setEditItem,
      readOnly,
    }),
    [addAuthHeader, removeAuthHeader, authHeaders, authHeaderFormItem, setEditItem, readOnly],
  );

  return (
    <TargetAuthHeaderContext.Provider value={value}>{children}</TargetAuthHeaderContext.Provider>
  );
};
